import { SlicedVisualization } from 'reporting-visualizations/visualizations/sliced/slicedRecords';
import I18n from 'I18n';
import { SupportedSlicedChartTypes, VisualizationTypes } from 'reporting-data/tsTypes/visualization';
import { isChartTypeColorTheme } from 'reporting-data/tsTypes/reportChart/color';
import { List, fromJS, Map as ImmutableMap } from 'immutable';
import { getPrimaryDataDataset, getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { pickColors, pickPatterns
//@ts-expect-error Untyped import
} from 'reporting-ui-components/reports/utils/colors/colorPicker';
//@ts-expect-error Untyped import
import { getDataKeys } from 'reporting-visualizations/highcharts/chartUtils';
import { getPaletteColorScheme } from './colorUtils';
import { isRelationalReport, isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { toMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { isBrandKitTheme } from 'reporting-ui-components/reports/utils/colors/brandKitUtils';
import { getReportColorEncoding, getReportValuesEncodings } from 'reporting-snowflake/relational/utils/report-utils';

//@ts-expect-error Untyped import
import { toColor } from 'reporting-snowflake/relational/visualize/visual-mappers/pie-mapper';
import { ColorOption } from 'reporting-snowflake/relational/schema/highcharts-settings';
import { THEME, PaletteIds } from 'reporting-data/tsTypes/visualization/colors';
import { createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
const DEFAULT_GAUGE_THEME_COLOR_OPTION = {
  theme: THEME.MULTI
};
const DEFAULT_GAUGE_PALETTE_COLOR_OPTION = {
  paletteId: PaletteIds.Categorical14
};
const stringifyDataKeys = keys => keys.map(k => String(k));
export const applySlicedColorsToStyle = (colorConfig, brandKitPalettes) => {
  const theme = getPaletteColorScheme(fromJS(colorConfig), brandKitPalettes);
  return theme && theme.length > 0 ? {
    colors: theme
  } : {};
};
const applyThemeToLayer = ({
  colorConfig,
  metrics,
  originalReport,
  primaryData,
  primaryColorField,
  brandKitPalettes
}) => {
  const theme = getPaletteColorScheme(fromJS(colorConfig), brandKitPalettes);
  const isBrandKitPalette = colorConfig.theme && isBrandKitTheme(colorConfig.theme);
  const pickBrandKitTheme = isBrandKitPalette && theme || undefined;
  const maybeUpdatedColorConfig = theme.get('colorsTheme') !== undefined ? Object.assign({}, colorConfig, {
    theme: theme.get('colorsTheme')
  }) : colorConfig;
  const definedPatterns = theme.get('patterns') ? pickPatterns(theme.get('patterns'), stringifyDataKeys(getDataKeys(primaryData, primaryColorField))) : undefined;
  if (isConfigReport(originalReport)) {
    const metricToMetricTypesMap = originalReport.getIn(List(['config', 'metrics'])).toMap().mapKeys((_, metric) => metric.get('property')).map(metric => metric.get('metricTypes'));
    const pickedColors = primaryColorField ? pickColors(fromJS(maybeUpdatedColorConfig), stringifyDataKeys(getDataKeys(primaryData, primaryColorField)), pickBrandKitTheme) : pickColors(fromJS(maybeUpdatedColorConfig).update('custom', custom => custom === null || custom === void 0 ? void 0 : custom.reduce((colors, color, key) => colors.merge(metricToMetricTypesMap.get(key, List()).toMap().mapKeys((_, v) => toMetricKey({
      property: key,
      type: v
    })).map(() => color)), ImmutableMap())), metrics, pickBrandKitTheme);
    return {
      color: {
        field: primaryColorField,
        defined: pickedColors,
        definedPatterns
      }
    };
  }
  const pickedColors = pickColors(fromJS(maybeUpdatedColorConfig), stringifyDataKeys(getDataKeys(primaryData, primaryColorField)), pickBrandKitTheme);
  return {
    color: {
      field: primaryColorField,
      defined: pickedColors,
      definedPatterns
    }
  };
};
const applyPaletteToLayer = ({
  originalReport,
  primaryData,
  colorConfig,
  brandKitPalettes
}) => {
  // TODO: support palette for non-relational reports
  if (isRelationalReport(originalReport)) {
    const relationalReportRecord = createRelationalReportFromBaseReport(originalReport);
    const color = getReportColorEncoding(relationalReportRecord);
    const values = getReportValuesEncodings(relationalReportRecord);
    const isGrouped = !!color;
    const colorColumn = isGrouped && color.column;
    const valueColumns = values === null || values === void 0 ? void 0 : values.map(v => v.column);
    return {
      color: toColor(colorColumn || undefined, valueColumns, primaryData, colorConfig ? ColorOption(colorConfig) : ColorOption(DEFAULT_GAUGE_PALETTE_COLOR_OPTION), brandKitPalettes)
    };
  }
  return {};
};
export const applySlicedColorsToLayer = ({
  primaryData,
  metrics,
  originalReport,
  primaryColorField,
  brandKitPalettes,
  colorConfig
}) => {
  if (isRelationalReport(originalReport)) {
    return applyPaletteToLayer({
      originalReport,
      primaryData,
      colorConfig: colorConfig !== null && colorConfig !== void 0 ? colorConfig : DEFAULT_GAUGE_PALETTE_COLOR_OPTION,
      brandKitPalettes
    });
  } else if (isChartTypeColorTheme(colorConfig)) {
    return applyThemeToLayer({
      primaryData,
      colorConfig,
      metrics,
      originalReport,
      primaryColorField,
      brandKitPalettes
    });
  }
  return applyThemeToLayer({
    colorConfig: DEFAULT_GAUGE_THEME_COLOR_OPTION,
    metrics,
    originalReport,
    primaryColorField,
    primaryData,
    brandKitPalettes
  });
};
const buildSlicedVisualization = ({
  datasetMap,
  partialViz,
  reportDetail,
  originalReport,
  brandKitPalettes,
  isMobile = false
}) => {
  var _dimensions$;
  const {
    dimensions,
    metrics,
    frequency,
    breakdown
  } = reportDetail;
  const {
    chartType,
    style,
    color,
    layer
  } = partialViz;
  const {
    grandTotalColumn,
    valueFormattingOptions,
    colorFormattingOptions
  } = layer || {};
  const colorField = (_dimensions$ = dimensions === null || dimensions === void 0 ? void 0 : dimensions[0]) !== null && _dimensions$ !== void 0 ? _dimensions$ : breakdown;
  const isGrouped = !!(colorField !== null && colorField !== void 0 && colorField.length);
  const primaryDataDataset = getPrimaryDataDataset(datasetMap);
  const visualizationPrimaryData = List.isList(primaryDataDataset) ? getPrimaryDataset(datasetMap) : primaryDataDataset;
  const angleFieldDefault = isGrouped ? metrics === null || metrics === void 0 ? void 0 : metrics[0] : I18n.text('property.count.label'); // if there is no layer, how it will get data from metrics[0]

  const defaultLayer = {
    angle: {
      field: angleFieldDefault !== null && angleFieldDefault !== void 0 ? angleFieldDefault : ''
    },
    dataset: 'primary',
    color: {
      field: colorField
    },
    style: {
      innerSize: chartType === SupportedSlicedChartTypes.DONUT ? '35%' : '0%'
    }
  };
  const defaultStyle = {
    isMobile,
    dateResolution: frequency,
    dataLabels: true
  };
  return SlicedVisualization({
    type: VisualizationTypes.SLICED,
    data: fromJS({
      primary: visualizationPrimaryData
    }),
    layer: Object.assign({}, defaultLayer, applySlicedColorsToLayer({
      primaryData: visualizationPrimaryData,
      metrics: metrics !== null && metrics !== void 0 ? metrics : [],
      originalReport,
      primaryColorField: colorField,
      brandKitPalettes,
      colorConfig: color
    }), colorFormattingOptions && {
      colorFormattingOptions
    }, valueFormattingOptions && {
      valueFormattingOptions
    }, {
      grandTotalColumn
    }),
    isGrouped,
    style: Object.assign({}, defaultStyle, style, color && applySlicedColorsToStyle(color, brandKitPalettes))
  });
};
export default buildSlicedVisualization;